// Restricted (c) Siemens Healthineers AG, 2025, All rights reserved.

import { NgClass } from '@angular/common';
import {
  CUSTOM_ELEMENTS_SCHEMA,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
} from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { DeepReadonly } from 'src/app/modules/shared/utils/api-call-state.util';
import { LocalizedDatePipe } from '../../../shared/pipes/localizedDate.pipe';
import { PatternMatchModelWithId } from '../../services/pattern-match-data/pattern-match-data.service';

export interface Item<TRaw = DeepReadonly<PatternMatchModelWithId>> {
  count: number;
  label: string;
  disabled: boolean;
  raw: TRaw;
}

export interface Bin<TRaw = DeepReadonly<PatternMatchModelWithId>> {
  date: Date;
  items: Item<TRaw>[];
}

export interface MouseEventData {
  bin: DeepReadonly<Bin>;
  item: Item;
}

@Component({
  selector: 'pm-chart',
  templateUrl: './pm-chart.component.html',
  styleUrls: ['./pm-chart.component.scss'],
  standalone: true,

  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [NgClass, TranslateModule, LocalizedDatePipe],
})
export class PatternMatchChartComponent implements OnDestroy, OnChanges {
  @Input() hoverMode: 'item' | 'bin' = 'bin';
  @Input({ required: true }) bins!: Bin[];
  @Input() maxLines = 6;
  @Output() itemHovered = new EventEmitter<MouseEventData | undefined>();
  @Output() itemClicked = new EventEmitter<MouseEventData>();

  // tallest bin
  maxCount = 0;
  lines: number[] = [];
  labels: string[] = [];
  destroy$ = new Subject<void>();

  constructor(private host: ElementRef) {}

  ngOnChanges(): void {
    // compute labels
    {
      const labels = new Set<string>();
      this.bins.forEach(({ items }) =>
        items.forEach(({ label }) => labels.add(label)),
      );
      this.labels = Array.from(labels.keys());
    }

    // compute max count
    {
      this.maxCount = Math.max(
        ...this.bins.map(({ items }) =>
          items.reduce((a, { count }) => a + count, 0),
        ),
      );
      (this.host.nativeElement as HTMLElement).style.setProperty(
        '--max-count',
        String(this.maxCount),
      );
    }

    // compute horizontal lines
    {
      let lines = new Array(this.maxCount + 1).fill(0).map((_, i) => i);
      // make sure we show no more than max lines
      while (lines.length > this.maxLines - 3) {
        lines = lines.filter((_, i) => !(i % 2));
      }

      lines = [0, 1, ...lines, this.maxCount];

      this.lines = lines;
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
